import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Images } from "../../../../config/images";
import TopImage from "../../common/TopImage";
import { handleSmartBack } from "../../../../utils/navigationHelper";

import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import "../../TeacherAdmin/classroom/Classroom.css";
import "../../TeacherAdmin/classroom/CreateClassroom.css";
import "./SchoolAdminCreateClassroom.css";
import SchoolAdminStudentSelector from "./SchoolAdminStudentSelector";
import CustomInput from "../../../common/CustomInput";
import { useGetClassroomById } from "../../../../hooks/teacher/classroom/useGetClassroomById";
import BackButton from "../Goback/GoBack";
import { Button, Spinner, Modal } from "react-bootstrap";
import useUpdateClassroom from "../../../../hooks/teacher/classroom/useUpdateClassroom";
import { useAssignStudents } from "../../../../hooks/teacher/classroom/useAssignStudents";
import SuccessModal from "../../../common/SuccessModal";
import { schoolAdminClassroomValidationSchema } from "./SchoolAdminCreateClassroom";
import { useGetQuizLimitStatus } from "../../../../hooks/quiz/useGetQuizLimitStatus";
import { useSetQuizLimitClassroom } from "../../../../hooks/quiz/useSetQuizLimitClassroom";
import ScrollToError from "../../../../utils/ScrollToError";
import ReassignTeacherModal from "./ReassignTeacherModal";

const SchoolAdminUpdateClassroom = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const topRef = useRef(null);
  const classroomNameRef = useRef(null);

  // Fetch classroom data
  const {
    classroom,
    loading: fetchLoading,
    error: fetchError,
    getClassroomById,
    setClassroom,
  } = useGetClassroomById();

  const { updateClassroom, loading, error } = useUpdateClassroom();
  const {
    assignStudents,
    loading: assignLoading,
    error: assignError,
    setError: setAssignError,
  } = useAssignStudents();

  const [successMessage, setSuccessMessage] = useState({
    message: "",
    description: "",
    type: "success",
  });
  const [showModal, setShowModal] = useState(false);
  const [refreshStudents, setRefreshStudents] = useState(false);

  // Quiz limit
  const {
    statusData: quizLimitStatus,
    loading: quizLimitStatusLoading,
    getQuizLimitStatus,
  } = useGetQuizLimitStatus();
  const { loading: quizLimitSetLoading, setQuizLimitClassroom } =
    useSetQuizLimitClassroom();
  const [showQuizLimitModal, setShowQuizLimitModal] = useState(false);
  /** @type {["form" | "success" | "error", Function]} */
  const [quizLimitModalState, setQuizLimitModalState] = useState("form");

  const canEditQuizLimit =
    !quizLimitStatusLoading && quizLimitStatus?.teacher_decides === true;

  // ─── Reassign Teacher — modal visibility only; logic lives in ReassignTeacherModal ───
  const [showReassignModal, setShowReassignModal] = useState(false);

  // Fetch classroom details and quiz limit status on mount
  useEffect(() => {
    if (id) {
      getClassroomById({ classroomId: id });
      getQuizLimitStatus({ classroom_id: id });
    }
  }, [id]);

  /**
   * Get initial values from fetched classroom data
   */
  const getInitialValues = () => {
    if (!classroom) {
      return {
        teacher: null,
        classroomName: "",
        students: [],
      };
    }

    const rawStudents = Array.isArray(classroom.students?.data)
      ? classroom.students.data
      : [];

    return {
      teacher: classroom.teacher || null,
      classroomName: classroom.name || "",
      students: rawStudents.map((student) => ({
        id: student.id,
        username: student.username,
        name: student.name,
        student_code: student.student_code,
      })),
    };
  };

  const handleUpdateClassroom = async (values) => {
    setSuccessMessage({
      message: "",
      description: "",
      type: "success",
    });
    setAssignError(null);

    // Get the original student IDs from the classroom data
    const originalStudentIds = new Set(
      classroom.students.data.map((student) => student.id),
    );

    // Get the current selected student IDs
    const currentStudentIds = new Set(
      values.students.map((student) => student.id),
    );

    // Find newly added students (in current but not in original)
    const studentsToAdd = values.students
      .filter((student) => !originalStudentIds.has(student.id))
      .map((student) => ({
        id: student.id,
        is_assign: true,
      }));

    // Find removed students (in original but not in current)
    const studentsToRemove = classroom.students.data
      .filter((student) => !currentStudentIds.has(student.id))
      .map((student) => ({
        id: student.id,
        is_assign: false,
      }));

    // Combine only the changed students
    const changedStudents = [...studentsToAdd, ...studentsToRemove];

    const response = await updateClassroom({
      classroomId: id,
      classroomData: {
        name: values.classroomName,
      },
    });

    if (response) {
      if (changedStudents.length > 0) {
        const assignStudentsResponse = await assignStudents({
          classroomId: id,
          studentsData: changedStudents,
        });

        if (assignStudentsResponse) {
          await getClassroomById({ classroomId: id });
          topRef.current?.scrollIntoView({ behavior: "smooth" });
          setSuccessMessage({
            message:
              "Classroom details and student assignments updated successfully",
            description: "",
            type: "success",
          });
          setShowModal(true);
        } else {
          topRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Only name changed, no student changes
        topRef.current?.scrollIntoView({ behavior: "smooth" });
        setSuccessMessage({
          message: "Classroom details updated successfully",
          description: "",
          type: "success",
        });
        setShowModal(true);
      }
    }
  };

  // Loading state
  if (fetchLoading && !classroom) {
    return (
      <div>
        <BackButton />
        <div className="classroom-cnt">
          <TopImage imageSrc={Images.TopImage} text="Update Classroom" />
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading classroom details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div>
        <BackButton />
        <TopImage imageSrc={Images.TopImage} text="Update Classroom" />
        <div className="custom-card mt-4">
          <div className="d-flex justify-content-center mt-3">
            <div className="text-center" style={{ maxWidth: "400px" }}>
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(220, 53, 69, 0.1)",
                }}
              >
                <i
                  className="bi bi-exclamation-triangle"
                  style={{ fontSize: "28px", color: "#dc3545" }}
                ></i>
              </div>
              <h5 className="mb-2" style={{ color: "#333", fontWeight: "600" }}>
                Unable to Load Classroom
              </h5>
              <p className="text-muted mb-4" style={{ fontSize: "14px" }}>
                {fetchError}
              </p>
              <button
                className="btn btn-outline-primary btn-sm px-4"
                onClick={() => getClassroomById({ classroomId: id })}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No classroom found
  if (!classroom) {
    return (
      <div>
        <BackButton />
        <TopImage imageSrc={Images.TopImage} text="Update Classroom" />
        <div className="custom-card mt-4">
          <div className="d-flex justify-content-center mt-3">
            <div className="text-center" style={{ maxWidth: "400px" }}>
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255, 193, 7, 0.15)",
                }}
              >
                <i
                  className="bi bi-folder-x"
                  style={{ fontSize: "28px", color: "#f59e0b" }}
                ></i>
              </div>
              <h5 className="mb-2" style={{ color: "#333", fontWeight: "600" }}>
                Classroom Not Found
              </h5>
              <p className="text-muted mb-4" style={{ fontSize: "14px" }}>
                The classroom you're looking for doesn't exist or may have been
                deleted.
              </p>
              <button
                className="btn btn-outline-secondary btn-sm px-4"
                onClick={() => handleSmartBack(navigate, "/school/classroom")}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div>
        <BackButton />
      </div>
      <div className="classroom-cnt">
        <TopImage imageSrc={Images.TopImage} text="Update Classroom" />

        <div className="mt-4">
          <div ref={topRef}>
            {/* Classroom Update Error */}
            {(error || assignError) && (
              <div className="alert alert-danger mt-3" role="alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error ? error : assignError}
              </div>
            )}
          </div>

          <div>
            <Formik
              initialValues={getInitialValues()}
              validationSchema={schoolAdminClassroomValidationSchema}
              onSubmit={handleUpdateClassroom}
              enableReinitialize={true}
            >
              {({ isSubmitting }) => (
                <Form>
                  <ScrollToError 
                    fieldRefs={{ classroomName: classroomNameRef }}
                  />
                  <div className="mt-0 input-container">
                    <CustomInput
                      type="text"
                      name="classroomName"
                      label="Classroom Name"
                      placeholder="Enter classroom name"
                      autoComplete="off"
                      layout="inline"
                      ref={classroomNameRef}
                    />
                  </div>

                  {/* ─── Current Teacher Display with Reassign Button ─── */}
                  <div
                    className="mt-3 input-container"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <label
                        className="form-label"
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#555",
                          marginBottom: "6px",
                          display: "block",
                        }}
                      >
                        Assigned Teacher
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "10px 14px",
                          background: "#f8f9fa",
                          border: "1px solid #dee2e6",
                          borderRadius: "8px",
                          fontSize: "14px",
                          color: "#333",
                        }}
                      >
                        <i className="bi bi-person-fill" style={{ color: "#6c757d" }}></i>
                        {classroom.teacher ? (
                          <span>
                            {classroom.teacher.firstName || classroom.teacher.name}{" "}
                            {classroom.teacher.lastName || ""}
                            {classroom.teacher.email && (
                              <span style={{ color: "#6c757d", marginLeft: 6 }}>
                                ({classroom.teacher.email})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: "#dc3545" }}>
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>
                            No teacher assigned
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-warning btn-sm"
                      style={{ whiteSpace: "nowrap", marginTop: "26px" }}
                      onClick={() => setShowReassignModal(true)}
                    >
                      <i className="bi bi-arrow-left-right me-1"></i>
                      Reassign Teacher
                    </button>
                  </div>

                  {/* Quiz Limit Display */}
                  {classroom.quiz_attempt_limit != null && (
                    <div
                      className="mt-3 input-container"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <CustomInput
                          type="number"
                          name="quizLimitDisplay"
                          label="Quiz Attempt Limit"
                          placeholder="Quiz Attempt Limit"
                          autoComplete="off"
                          layout="inline"
                          disabled={true}
                          value={classroom.quiz_attempt_limit}
                        />
                      </div>
                      {canEditQuizLimit && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          style={{ whiteSpace: "nowrap" }}
                          onClick={() => {
                            setQuizLimitModalState("form");
                            setShowQuizLimitModal(true);
                          }}
                        >
                          <i className="bi bi-pencil-square me-1"></i>
                          Edit
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-4">
                    <SchoolAdminStudentSelector
                      name="students"
                      teacherFieldName="teacher"
                      initialTeacher={classroom.teacher}
                      readOnlyTeacher={true}
                      hideTeacherSection={true}
                      initialSelectedStudents={classroom.students}
                      refresh={refreshStudents}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="custom-primary-button mt-4 mb-2"
                    disabled={isSubmitting || assignLoading || loading}
                  >
                    {isSubmitting || assignLoading || loading
                      ? "Updating Classroom..."
                      : "Update Classroom"}
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        <SuccessModal
          type={successMessage.type}
          showModal={showModal}
          handleClose={setShowModal}
          title={successMessage.message}
          message={successMessage.description}
          primaryButtonText="Continue Editing"
          secondaryButtonText="Classrooms List"
          secondaryButtonLink="/school-admin/classroom"
          handlePrimaryClickCallback={() => {
            setRefreshStudents((prev) => !prev);
          }}
        />

        {/* Quiz Limit Edit Modal */}
        <Modal
          show={showQuizLimitModal}
          onHide={() => setShowQuizLimitModal(false)}
          centered
          className="custom-modal"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Body className="p-2 m-4">
            {quizLimitModalState === "form" && (
              <>
                <div className="text-center mb-2">
                  <h5 className="fw-bold fs-3">Update Quiz Limit</h5>
                </div>
                <p className="text-center mb-4 mt-2 pt-2 custom-modal-description">
                  Set the maximum number of quiz attempts for this classroom.
                </p>
                <Formik
                  initialValues={{
                    quizLimit: classroom?.quiz_attempt_limit || "",
                  }}
                  validationSchema={Yup.object().shape({
                    quizLimit: Yup.number()
                      .typeError("Must be a number")
                      .required("Quiz limit is required")
                      .min(1, "Must be at least 1")
                      .max(10, "Must be at most 10")
                      .integer("Must be a whole number"),
                  })}
                  onSubmit={async (values, { setSubmitting }) => {
                    await setQuizLimitClassroom(
                      {
                        classroom_id: id,
                        limit: Number(values.quizLimit),
                      },
                      (success) => {
                        if (success) {
                          getClassroomById({ classroomId: id });
                          setQuizLimitModalState("success");
                        } else {
                          setQuizLimitModalState("error");
                        }
                      },
                    );
                    setSubmitting(false);
                  }}
                  enableReinitialize
                >
                  {({ isSubmitting, errors, touched }) => (
                    <Form>
                      <div className="mb-4">
                        <label htmlFor="quizLimit" className="form-label">
                          Quiz Limit (1-10)
                        </label>
                        <Field
                          type="number"
                          name="quizLimit"
                          className={`form-control ${touched.quizLimit && errors.quizLimit ? "is-invalid" : ""}`}
                          placeholder="Enter quiz limit"
                          min="1"
                          max="10"
                        />
                        <ErrorMessage
                          name="quizLimit"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                        <button
                          type="button"
                          className="custom-cancel-button"
                          onClick={() => setShowQuizLimitModal(false)}
                          disabled={isSubmitting || quizLimitSetLoading}
                        >
                          Cancel
                        </button>
                        <Button
                          type="submit"
                          className="custom-primary-button"
                          disabled={isSubmitting || quizLimitSetLoading}
                        >
                          {isSubmitting || quizLimitSetLoading
                            ? "Saving..."
                            : "Save"}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </>
            )}

            {quizLimitModalState === "success" && (
              <>
                <div className="text-center mb-2">
                  <h5 className="text-success fw-bold fs-3">
                    Quiz Limit Updated!
                  </h5>
                </div>
                <p className="text-center mb-4 mt-2 pt-2 custom-modal-description">
                  The quiz limit has been updated successfully.
                </p>
                <div className="d-flex justify-content-center">
                  <Button
                    className="custom-primary-button w-auto"
                    onClick={() => setShowQuizLimitModal(false)}
                  >
                    Continue
                  </Button>
                </div>
              </>
            )}

            {quizLimitModalState === "error" && (
              <>
                <div className="text-center mb-2">
                  <h5 className="text-danger fw-bold fs-3">Update Failed</h5>
                </div>
                <p className="text-center mb-4 mt-2 pt-2 custom-modal-description">
                  There was an error updating the quiz limit. Please try again.
                </p>
                <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                  <button
                    className="custom-cancel-button"
                    onClick={() => setShowQuizLimitModal(false)}
                  >
                    Close
                  </button>
                  <Button
                    className="custom-primary-button"
                    onClick={() => setQuizLimitModalState("form")}
                  >
                    Retry
                  </Button>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>

      <ReassignTeacherModal
        show={showReassignModal}
        onHide={() => setShowReassignModal(false)}
        classroomId={id}
        currentTeacherId={classroom?.teacher?.id}
        onReassignSuccess={() => {
          getClassroomById({ classroomId: id });
          getQuizLimitStatus({ classroom_id: id });
          setRefreshStudents((prev) => !prev);
        }}
      />
    </div>
  );
};

export default SchoolAdminUpdateClassroom;
